import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import Home from '@/views/home.vue'
import Login from '@/views/login.vue'
import SsoLogin from '@/views/login/sso.vue'
import config from '@/config'
import { storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { i18n } from '@/i18n'

/** id из params: без id → null (не NaN от Number(undefined)) */
function routeParamId(rt, key = 'id') {
  const raw = rt.params?.[key]
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const router = createRouter({
  history: createWebHistory(config.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/activities',
      name: 'activities.list',
      component: () => import('@/views/activities/list.vue'),
      meta: {
        titleKey: 'routes.activities.list',
        icon: 'ui-checks-grid',
        sidebar: true,
        rbac: 'activities_view',
      },
    },
    {
      path: '/activities/:id(\\d+)?',
      name: 'activities.item',
      component: () => import('@/views/activities/item.vue'),
      props: rt => ({ id: routeParamId(rt) }),
      meta: { titleKey: 'routes.activities.item' },
    },
    {
      path: '/plans',
      name: 'plans',
      component: () => import('@/views/plans/list.vue'),
      meta: { titleKey: 'routes.plans.list' },
    },
    {
      path: '/plans/:id(\\d+)',
      name: 'plans.item',
      component: () => import('@/views/plans/item.vue'),
      props: rt => ({ id: routeParamId(rt) }),
      meta: { titleKey: 'routes.plans.item' },
    },
    {
      path: '/orders',
      name: 'orders.list',
      component: () => import('@/views/orders/list.vue'),
      meta: {
        titleKey: 'routes.orders.list',
        icon: 'pencil-square',
        sidebar: true,
        rbac: 'orders_view',
      },
    },
    {
      path: '/orders/:id(\\d+)',
      name: 'orders.item',
      component: () => import('@/views/orders/item.vue'),
      props: rt => ({ id: routeParamId(rt), newOrder: rt.query.newOrder }),
      meta: { titleKey: 'routes.orders.item' },
    },
    {
      path: '/orders/:id(\\d+)/cost',
      name: 'orders.item.cost',
      component: () => import('@/views/orders/item/cost.vue'),
      props: rt => ({ id: routeParamId(rt) }),
      meta: { titleKey: 'routes.orders.item.cost', rbac: ['buh', 'coordinator'] },
    },
    {
      path: '/stock',
      name: 'stock.list',
      meta: {
        titleKey: 'routes.stock.list',
        icon: 'box-seam',
        sidebar: true,
        rbac: 'stock_view',
      },
      children: [
        {
          path: '/stock/medicines',
          name: 'stock.medicines.list',
          component: () => import('@/views/stock/medicines/list.vue'),
          meta: {
            titleKey: 'routes.stock.medicines.list',
            sidebar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/medicines/:id(\\d+)?',
          name: 'stock.medicines.item',
          component: () => import('@/views/stock/medicines/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.stock.medicines.item' },
        },
        {
          path: '/stock/materials_nd',
          name: 'stock.materials_nd.list',
          component: () => import('@/views/stock/materials_nd/list.vue'),
          meta: {
            titleKey: 'routes.stock.materials_nd.list',
            sidebar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/materials_nd/:id(\\d+)?',
          name: 'stock.materials_nd.item',
          component: () => import('@/views/stock/materials_nd/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.stock.materials_nd.item' },
        },
        {
          path: '/stock/materials_comm',
          name: 'stock.materials_comm.list',
          component: () => import('@/views/stock/materials_comm/list.vue'),
          meta: {
            titleKey: 'routes.stock.materials_comm.list',
            sidebar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/materials_comm/:id(\\d+)?',
          name: 'stock.materials_comm.item',
          component: () => import('@/views/stock/materials_comm/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.stock.materials_comm.item' },
        },
        {
          path: '/stock/devices',
          name: 'stock.devices.list',
          component: () => import('@/views/stock/devices/list.vue'),
          meta: {
            titleKey: 'routes.stock.devices.list',
            sidebar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/devices/:id(\\d+)?',
          name: 'stock.devices.item',
          component: () => import('@/views/stock/devices/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.stock.devices.item' },
        },
        {
          path: '/stock/consumables',
          name: 'stock.consumables.list',
          component: () => import('@/views/stock/consumables/list.vue'),
          meta: {
            titleKey: 'routes.stock.consumables.list',
            sidebar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/consumables/:id(\\d+)?',
          name: 'stock.consumables.item',
          component: () => import('@/views/stock/consumables/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.stock.consumables.item' },
        },
        {
          path: '/stock/all',
          name: 'stock.all.list',
          component: () => import('@/views/stock/all/list.vue'),
          meta: {
            titleKey: 'routes.stock.all.list',
            sidebar: true,
            rbac: 'coordinator',
          },
        },
      ],
    },
    {
      path: '/registry',
      name: 'registry.list',
      meta: {
        titleKey: 'routes.registry.list',
        icon: 'list-task',
        sidebar: true,
        rbac: ['receptions_view', 'purchases_view', 'destructions_view', 'manager', 'supermanager', 'common'],
      },
      children: [
        {
          path: '/registries/inhouse',
          name: 'registries.inhouse.list',
          component: () => import('@/views/registries/inhouse/list.vue'),
          meta: {
            titleKey: 'routes.registries.inhouse.list',
            icon: 'flask',
            sidebar: true,
            rbac: 'inhouse_registry_view',
          },
        },
        {
          path: '/registries/materials_mc',
          name: 'registries.materials_mc.list',
          component: () => import('@/views/registries/materials_mc/list.vue'),
          meta: {
            titleKey: 'routes.registries.materials_mc.list',
          },
        },
        {
          path: '/registries/materials_mc/new',
          name: 'registries.materials_mc.item.new',
          component: () => import('@/views/registries/materials_mc/item.vue'),
          meta: { titleKey: 'routes.registries.item.new' },
        },
        {
          path: '/registries/materials_mc/:id(\\d+)',
          name: 'registries.materials_mc.item',
          component: () => import('@/views/registries/materials_mc/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.registries.item' },
        },
        {
          path: '/registries/reg_samples',
          name: 'registries.reg_samples.list',
          component: () => import('@/views/registries/reg_samples/list.vue'),
          meta: {
            titleKey: 'routes.registries.reg_samples.list',
          },
        },
        {
          path: '/registries/reg_samples/new',
          name: 'registries.reg_samples.item.new',
          component: () => import('@/views/registries/reg_samples/item.vue'),
          meta: { titleKey: 'routes.registries.item.new' },
        },
        {
          path: '/registries/reg_samples/:id(\\d+)',
          name: 'registries.reg_samples.item',
          component: () => import('@/views/registries/reg_samples/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.registries.item' },
        },
        {
          path: '/registries/export_control',
          name: 'registries.export_control.list',
          component: () => import('@/views/registries/export_control/list.vue'),
          meta: {
            titleKey: 'routes.registries.export_control.list',
            icon: 'bag-check',
            sidebar: true,
            rbac: 'export_control_view',
          },
        },
        {
          path: '/registries/export_control/new',
          name: 'registries.export_control.item.new',
          component: () => import('@/views/registries/export_control/item.vue'),
          meta: { titleKey: 'routes.registries.item.new' },
        },
        {
          path: '/registries/export_control/:id(\\d+)',
          name: 'registries.export_control.item',
          component: () => import('@/views/registries/export_control/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.registries.item' },
        },
        {
          path: '/purchases',
          name: 'purchases.list',
          component: () => import('@/views/purchases/list.vue'),
          meta: {
            titleKey: 'routes.purchases.list',
            icon: 'cart',
            sidebar: true,
            rbac: 'purchases_view',
          },
        },
        {
          path: '/purchases/:id(\\d+)',
          name: 'purchases.item',
          component: () => import('@/views/purchases/item.vue'),
          props: rt => ({ id: routeParamId(rt), newOrder: rt.query.newOrder }),
          meta: { titleKey: 'routes.purchases.item' },
        },
        {
          path: '/receptions',
          name: 'receptions.list',
          component: () => import('@/views/receptions/list.vue'),
          meta: {
            icon: 'file-earmark-medical',
            titleKey: 'routes.receptions.list',
            sidebar: true,
            rbac: 'receptions_view',
          },
        },
        {
          path: '/destructions_stock',
          name: 'destructions_stock.list',
          component: () => import('@/views/destructions_stock/list.vue'),
          meta: {
            icon: 'trash',
            titleKey: 'routes.destructions_stock.list',
            sidebar: true,
            rbac: 'destructions_view',
          },
        },
      ],
    },
    {
      path: '/catalog',
      name: 'catalog.list',
      meta: {
        titleKey: 'routes.catalog.list',
        icon: 'file-earmark-medical',
        sidebar: true,
        rbac: 'catalog_view',
      },
      children: [
        {
          path: '/catalog/medicine_trademarks',
          name: 'catalog.medicine_trademarks.list',
          component: () => import('@/views/catalog/medicine_trademarks/list.vue'),
          meta: {
            titleKey: 'routes.catalog.medicine_trademarks.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicine_trademarks/new',
          name: 'catalog.medicine_trademarks.new',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: () => ({ id: null }),
          meta: { titleKey: 'routes.catalog.medicine_trademarks.new' },
        },
        {
          path: '/catalog/medicine_trademarks/:id(\\d+)?',
          name: 'catalog.medicine_trademarks.item',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.medicine_trademarks.item' },
        },
        {
          path: '/catalog/medicines',
          name: 'catalog.medicines.list',
          component: () => import('@/views/catalog/medicines/list.vue'),
          meta: {
            titleKey: 'routes.catalog.medicines.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicines/:id(\\d+)?',
          name: 'catalog.medicines.item',
          component: () => import('@/views/catalog/medicines/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.medicines.item' },
        },
        {
          path: '/catalog/materials_nd',
          name: 'catalog.materials_nd.list',
          component: () => import('@/views/catalog/materials_nd/list.vue'),
          meta: {
            titleKey: 'routes.catalog.materials_nd.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/materials_nd/:id(\\d+)?',
          name: 'catalog.materials_nd.item',
          component: () => import('@/views/catalog/materials_nd/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.materials_nd.item' },
        },
        {
          path: '/catalog/materials_comm',
          name: 'catalog.materials_comm.list',
          component: () => import('@/views/catalog/materials_comm/list.vue'),
          meta: {
            titleKey: 'routes.catalog.materials_comm.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/materials_comm/:id(\\d+)?',
          name: 'catalog.materials_comm.item',
          component: () => import('@/views/catalog/materials_comm/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.materials_comm.item' },
        },
        {
          path: '/catalog/devices',
          name: 'catalog.devices.list',
          component: () => import('@/views/catalog/devices/list.vue'),
          meta: {
            titleKey: 'routes.catalog.devices.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/devices/:id(\\d+)?',
          name: 'catalog.devices.item',
          component: () => import('@/views/catalog/devices/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.devices.item' },
        },
        {
          path: '/catalog/consumables',
          name: 'catalog.consumables.list',
          component: () => import('@/views/catalog/consumables/list.vue'),
          meta: {
            titleKey: 'routes.catalog.consumables.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/consumables/:id(\\d+)?',
          name: 'catalog.consumables.item',
          component: () => import('@/views/catalog/consumables/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.consumables.item' },
        },
        {
          path: '/catalog/manufacturers',
          name: 'catalog.manufacturers.list',
          component: () => import('@/views/catalog/manufacturers/list.vue'),
          meta: {
            title: 'Производители',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/manufacturers/:id(\\d+)?',
          name: 'catalog.manufacturers.item',
          component: () => import('@/views/catalog/manufacturers/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Производитель' },
        },
        {
          path: '/catalog/vendors',
          name: 'catalog.vendors.list',
          component: () => import('@/views/catalog/vendors/list.vue'),
          meta: {
            titleKey: 'routes.catalog.vendors.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/vendors/:id(\\d+)?',
          name: 'catalog.vendors.item',
          component: () => import('@/views/catalog/vendors/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.vendors.item' },
        },
        {
          path: '/catalog/laboratories',
          name: 'catalog.laboratories.list',
          component: () => import('@/views/catalog/laboratories/list.vue'),
          meta: {
            titleKey: 'routes.catalog.laboratories.list',
            sidebar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/laboratories/:id(\\d+)?',
          name: 'catalog.laboratories.item',
          component: () => import('@/views/catalog/laboratories/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.laboratories.item' },
        },
        {
          path: '/templates',
          name: 'templates.list',
          component: () => import('@/views/templates/list.vue'),
          meta: {
            titleKey: 'routes.templates.list',
            sidebar: true,
            rbac: 'templates_view',
          },
        },
        {
          path: '/templates/:id(\\d+)?',
          name: 'templates.item',
          component: () => import('@/views/templates/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.templates.item' },
        },
        {
          path: '/catalog/activity_types',
          name: 'catalog.activity_types.list',
          component: () => import('@/views/catalog/activity_types/list.vue'),
          meta: {
            titleKey: 'routes.catalog.activity_types.list',
            sidebar: true,
            rbac: 'sa',
          },
        },
        {
          path: '/catalog/activity_types/:id(\\d+)?',
          name: 'catalog.activity_types.item',
          component: () => import('@/views/catalog/activity_types/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.catalog.activity_types.item' },
        },
        {
          path: '/dropdowns',
          name: 'dropdowns.list',
          component: () => import('@/views/dropdowns/list.vue'),
          meta: {
            titleKey: 'routes.dropdowns.list',
            sidebar: true,
            rbac: 'sa',
          },
        },
        {
          path: '/users',
          name: 'users.list',
          component: () => import('@/views/users/list.vue'),
          meta: {
            titleKey: 'routes.users.list',
            sidebar: true,
            rbac: 'users_edit',
          },
        },
        {
          path: '/users-updating',
          name: 'users.updating',
          component: () => import('@/views/users/usersUpdating.vue'),
          meta: {
            titleKey: 'routes.users.updating',
            sidebar: true,
            rbac: 'account_updater',
          },
        },
        {
          path: '/users/:id(\\d+)?',
          name: 'users.item',
          component: () => import('@/views/users/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.users.item' },
        },
        {
          path: '/current-user/',
          name: 'profile.item',
          component: () => import('@/views/users/profile.vue'),
          meta: { titleKey: 'routes.profile.item' },
        },
      ],
    },
    {
      path: '/receptions/:id(\\d+)?',
      name: 'receptions.item',
      component: () => import('@/views/receptions/item.vue'),
      props: rt => ({ id: routeParamId(rt) }),
      meta: {
        titleKey: 'routes.receptions.item',
        sidebar: false,
        rbac: ['receptions_edit', 'receptions_view', 'vortex_stock'],
      },
    },
    {
      path: '/analitycs',
      name: 'analitycs.list',
      meta: {
        titleKey: 'routes.analitycs.list',
        icon: 'file-bar-graph',
        sidebar: true,
        rbac: 'reports_view',
      },
      children: [
        {
          path: '/analitycs/tracker_expenses',
          name: 'analitycs.tracker_expenses.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'tracker_expenses' },
          meta: {
            titleKey: 'routes.analitycs.tracker_expenses.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
        {
          path: '/analitycs/writeoffs',
          name: 'analitycs.writeoffs.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'writeoffs' },
          meta: {
            titleKey: 'routes.analitycs.writeoffs.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
        {
          path: '/analitycs/coordinator_po',
          name: 'analitycs.coordinator_po.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'coordinator_po' },
          meta: {
            titleKey: 'routes.analitycs.coordinator_po.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
        {
          path: '/analitycs/fin_po',
          name: 'analitycs.fin_po.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'fin_po' },
          meta: {
            titleKey: 'routes.analitycs.fin_po.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
        {
          path: '/analitycs/gtd_buh',
          name: 'analitycs.gtd_buh.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'gtd_buh' },
          meta: {
            titleKey: 'routes.analitycs.gtd_buh.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
        {
          path: '/analitycs/testing_status',
          name: 'analitycs.testing_status.list',
          component: () => import('@/views/analitycs/reports.vue'),
          props: { report: 'testing_status' },
          meta: {
            titleKey: 'routes.analitycs.testing_status.list',
            sidebar: true,
            rbac: 'reports_view',
          },
        },
      ],
    },
    {
      path: '/documents',
      name: 'documents.list',
      meta: {
        titleKey: 'routes.documents.list',
        icon: 'file-earmark-richtext',
        sidebar: true,
        rbac: 'common',
      },
      children: [
        {
          path: '/documents/debiting',
          name: 'documents.debiting.list',
          component: () => import('@/views/documents/debitingList.vue'),
          meta: {
            titleKey: 'routes.documents.debiting.list',
            sidebar: true,
            rbac: ['writeoffs_view'],
          },
        },
        {
          path: '/documents/commissioning',
          name: 'documents.commissioning.list',
          component: () => import('@/views/documents/debitingList.vue'),
          props: { commissioning: true },
          meta: {
            titleKey: 'routes.documents.commissioning.list',
            sidebar: true,
            rbac: ['writeoffs_view'],
          },
        },
        {
          path: '/documents/debiting/:id(\\d+)?',
          name: 'documents.debiting.item',
          component: () => import('@/views/documents/debitingItem.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: {
            titleKey: 'routes.documents.debiting.item',
            sidebar: false,
            rbac: ['writeoffs_edit', 'writeoffs_view'],
          },
        },
        {
          path: '/documents/commissioning/:id(\\d+)?',
          name: 'documents.commissioning.item',
          component: () => import('@/views/documents/debitingItem.vue'),
          props: rt => ({ id: routeParamId(rt), commissioning: true }),
          meta: {
            titleKey: 'routes.documents.commissioning.item',
            sidebar: false,
            rbac: ['writeoffs_edit', 'writeoffs_view'],
          },
        },
        {
          path: '/documents/transfer',
          name: 'documents.transfer.list',
          component: () => import('@/views/documents/transferList.vue'),
          meta: {
            titleKey: 'routes.documents.transfer.list',
            sidebar: true,
            rbac: ['acts_pp_view', 'acts_pp_edit'],
          },
        },
        {
          path: '/documents/referral',
          name: 'documents.referral.list',
          component: () => import('@/views/documents/referralList.vue'),
          meta: {
            title: 'Направительные письма',
            sidebar: true,
            rbac: ['coordinator', 'orders_view'],
          },
        },
        {
          path: '/destructions',
          name: 'destructions.list',
          component: () => import('@/views/destructions/list.vue'),
          meta: {
            titleKey: 'routes.destructions.list',
            sidebar: true,
            rbac: ['destructions_view', 'manager', 'supermanager'],
          },
        },
        {
          path: '/destructions/:id(\\d+)?',
          name: 'destructions.item',
          component: () => import('@/views/destructions/item.vue'),
          props: rt => ({ id: routeParamId(rt) }),
          meta: { titleKey: 'routes.destructions.item' },
        },
      ],
    },
    {
      path: '/archive',
      name: 'archive.list',
      component: () => import('@/views/archive/list.vue'),
      meta: {
        titleKey: 'routes.archive.list',
        icon: 'archive',
        sidebar: true,
        rbac: 'archive_view',
      },
    },
    {
      path: '/news',
      name: 'news.list',
      component: () => import('@/views/news/list.vue'),
      meta: {
        titleKey: 'routes.news.list',
        icon: 'newspaper',
        sidebar: true,
        rbac: 'common',
      },
    },
    {
      path: '/tickets',
      name: 'tickets.list',
      component: () => import('@/views/tickets/list.vue'),
      meta: {
        titleKey: 'routes.tickets.list',
        icon: 'ticket-perforated',
      },
    },
    {
      path: '/tickets/support',
      name: 'tickets.support',
      component: () => import('@/views/tickets/support.vue'),
      meta: { titleKey: 'routes.tickets.support' },
    },
    {
      path: '/tickets/new',
      name: 'tickets.create',
      component: () => import('@/views/tickets/item.vue'),
      props: () => ({ id: null }),
      meta: { titleKey: 'routes.tickets.create' },
    },
    {
      path: '/tickets/:id(\\d+)',
      name: 'tickets.item',
      component: () => import('@/views/tickets/item.vue'),
      props: rt => ({ id: routeParamId(rt) }),
      meta: { titleKey: 'routes.tickets.item' },
    },
    {
      path: '/sso_login',
      name: 'sso_login',
      component: SsoLogin,
    },
    {
      path: '/:notFoundPath(.*)',
      name: 'NotFound',
      component: () => import('@/views/notFound.vue'),
    },
  ],
  strict: true,
  scrollBehavior(to, from) {
    if (to.name === from.name) {
      return null
    }
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(to => {
  const userStore = useUserStore()
  const { isAuthorized } = storeToRefs(userStore)
  const { returnUrl } = storeToRefs(useUiStore())

  if (to.name === 'sso_login') {
    return true
  }

  if (isAuthorized.value && to.name === 'login') {
    if (returnUrl.value !== undefined) {
      const target = returnUrl.value
      returnUrl.value = undefined
      return target
    }
    return { name: 'home' }
  }

  // отключен возврат на страницу с которой был логаут. Сказали не надо такого
  // if (
  //   !isAuthorized.value &&
  //   ['login', 'sso_login'].includes(to.name) &&
  //   returnUrl.value === undefined
  // ) {
  //   returnUrl.value = from
  // }

  // Сохраняем returnUrl только один раз при первом неавторизованном доступе
  if (!isAuthorized.value && to.name !== 'login' && to.name !== 'sso_login') {
    if (returnUrl.value === undefined) {
      returnUrl.value = to
    }
    return { name: 'login' }
  }

  if (
    isAuthorized.value &&
    to.name === 'users.item' &&
    to.params.id &&
    userStore.can('account_updater') &&
    !userStore.can('sa') &&
    !userStore.can('users_edit')
  ) {
    return { name: 'users.updating' }
  }
})

const defaultTitle = 'SOYKA:RTMS'
router.afterEach(async to => {
  await nextTick()
  const pageTitle = to.meta.titleKey
    ? i18n.global.t(String(to.meta.titleKey))
    : to.meta.title
  document.title = pageTitle ? `${pageTitle} :: ${defaultTitle}` : defaultTitle
})

export default router
